import { useMemo } from 'react';
import { useFeatureValue } from '@shared/core/featureFlags';
import type { ProductRow } from '../../PrimaryDashboardContent/PrimaryDashboardContent.types';
import { useGuestListRsvpProductRow } from './useGuestListRsvpProductRow';
import { useInvitationsProductRow } from './useInvitationsProductRow';
import { useRegistryProductRow } from './useRegistryProductRow';
import { useWebsiteProductRow } from './useWebsiteProductRow';
import type { ProductVerticals } from '@shared/core/productVerticals/productVerticals.types';
import { useIsContactCollectionMessageEnabled } from '@shared/hooks/useIsContactCollectionMessageEnabled';
import { useIsSmsEnabled } from '@shared/hooks/useIsSmsEnabled';
import { useGetMessagingTierQuery } from '@apps/admin/routes/groupMessaging/GroupMessaging.graphql.generated';
import { MessagingTier } from '@graphql/generated-base';
import type { EventPageType, EventPageVisibility } from '@graphql/generated-base';

export type PageVisibility = `${EventPageVisibility}`;

export interface PageInfo {
  id: string;
  pageSlug: string;
  visibility: PageVisibility;
  type?: EventPageType;
  pageTitle?: string;
  createdAt?: {
    timestamp: string;
  };
  photo?: {
    id: string;
    url: string;
  } | null;
}
export function useProductRows(eventPages: ReadonlyArray<PageInfo>, productVerticals: ProductVerticals, eventId?: string, pendingRefundOrdersCount?: number) {
  const { value: enableRsvpReminderMessage } = useFeatureValue('enableRsvpReminderMessage');
  const { data: tierData } = useGetMessagingTierQuery({
    variables: { eventId: eventId ?? '' },
    skip: !eventId,
    batchMode: 'fast',
  });
  const hasMessagingPurchase = tierData?.getMessagingSettingsByEventId?.messagingTier === MessagingTier.messagingPlus;
  const [isSmsChannelEnabled] = useIsSmsEnabled(hasMessagingPurchase);
  const [enableContactCollectionMessage] = useIsContactCollectionMessageEnabled();

  const websiteProductRow = useWebsiteProductRow(eventPages, productVerticals);
  const registryProductRow = useRegistryProductRow(eventPages, productVerticals, pendingRefundOrdersCount);
  const invitationsProductRow = useInvitationsProductRow({
    productVerticals,
    hotelBlockReminderEnabled: true,
    enableRsvpReminderMessage: enableRsvpReminderMessage === 'on',
    enableContactCollectionMessage,
    isSmsChannelEnabled,
    eventId,
  });
  const guestListRsvpProductRow = useGuestListRsvpProductRow(eventPages, productVerticals);

  const productRows = useMemo<ReadonlyArray<ProductRow>>(
    () =>
      [websiteProductRow, registryProductRow, invitationsProductRow, guestListRsvpProductRow].sort((a, b) => {
        return (b.isEnabled ? 1 : 0) - (a.isEnabled ? 1 : 0);
      }),
    [websiteProductRow, registryProductRow, invitationsProductRow, guestListRsvpProductRow],
  );

  return productRows;
}
